// Pure helpers for the What's New feature. No React, no network — everything
// here is a total function over the server payload, which is what makes the
// self-hiding contract (D3) testable in isolation.

/**
 * The single gate that turns "anything went wrong" into an empty list.
 *
 * D3 says a missing endpoint, a non-200, a malformed body and an empty result
 * set are ONE behaviour: the feature renders nothing. Concentrating that here
 * means no downstream surface needs an error branch — the panel, the tab and
 * the drawer all just receive `[]` and draw nothing.
 *
 * A release without a string `version` is dropped rather than repaired: the
 * version IS the identity (D8), so an entry without one cannot be keyed,
 * marked read, or deduplicated.
 */
export const normaliseReleases = (raw) => {
  if (!Array.isArray(raw)) return [];

  // Deduplicate on version. The version is the identity (D8) — it is the React
  // key, the read-state key and what the unread count counts. A server that
  // serves the same version twice would otherwise produce duplicate React keys,
  // double-count the release in the badge, and then under-count it the moment
  // it is marked read (one entry in the read set collapsing two list rows).
  const seenVersions = new Set();

  return raw
    .filter((r) => r && typeof r.version === 'string' && r.version.length > 0)
    .filter((r) => {
      if (seenVersions.has(r.version)) return false;
      seenVersions.add(r.version);
      return true;
    })
    .map((r) => ({
      version: r.version,
      // Kept as the raw ISO string; formatting is the view's job and needs a
      // locale this module does not have.
      releasedAt: typeof r.releasedAt === 'string' ? r.releasedAt : null,
      title: typeof r.title === 'string' ? r.title : '',
      highlights: Array.isArray(r.highlights)
        ? r.highlights
            .filter((h) => h && typeof h.title === 'string')
            .map((h, i) => ({
              // The server's `id` is preferred, but a highlight without one is
              // still renderable — fall back to a positional key rather than
              // dropping content the user is entitled to read.
              id: typeof h.id === 'string' && h.id.length > 0 ? h.id : `${r.version}-${i}`,
              title: h.title,
              body: typeof h.body === 'string' ? h.body : '',
            }))
        : [],
    }));
};

/**
 * D7: server prose carries `{{brand}}` and nothing else, so the client
 * interpolates it.
 *
 * The brand gate (`scripts/lint-brand.js`) only scans `src/`, so a hardcoded
 * "Carbon Glance" arriving over the wire would sail past CI and render in a
 * Faradai build — which is the exact failure the gate exists to prevent. The
 * placeholder moves that decision to render time, where the brand is known.
 *
 * A literal replace, not a template evaluator: the input is untrusted server
 * text and must never be executed.
 */
export const applyBrand = (text, brand) => {
  if (typeof text !== 'string') return '';
  return text.split('{{brand}}').join(brand ?? '');
};

/** Releases the browser has not recorded as read. Pure — the store is passed in. */
export const selectUnread = (releases, readVersions) => {
  const read = new Set(Array.isArray(readVersions) ? readVersions : []);
  return (Array.isArray(releases) ? releases : []).filter((r) => !read.has(r.version));
};

export const RECENT_WINDOW_DAYS = 30;

/**
 * True for a release published within the last `days`.
 *
 * A missing or unparseable date returns **false** — deliberately. The one
 * caller is the first-run back-fill, where "not recent" means "mark it read",
 * so an undated release is treated as old history rather than being announced
 * to a brand-new user as news.
 */
export const isWithinLastDays = (releasedAt, days, now = Date.now()) => {
  if (typeof releasedAt !== 'string' || !releasedAt) return false;
  const t = Date.parse(releasedAt);
  if (Number.isNaN(t)) return false;
  return now - t <= days * 24 * 60 * 60 * 1000;
};

/**
 * The first-run back-fill.
 *
 * A brand-new browser has an empty read state, so *every* release is unread and
 * the post-login drawer would greet a first-time user with the entire product
 * history — the opposite of "what's new". This returns the versions to mark
 * read on that very first load: everything except the genuinely recent ones.
 *
 * Runs once (guarded by `seeded` on the store); an existing user is unaffected
 * because their read state is already populated.
 */
export const seedReadState = (releases, now = Date.now()) =>
  (Array.isArray(releases) ? releases : [])
    .filter((r) => !isWithinLastDays(r.releasedAt, RECENT_WINDOW_DAYS, now))
    .map((r) => r.version);
